import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MentorPortalNav from './MentorPortalNav';
import MentorMessagesChrome from './MentorMessagesChrome';
import MentorMessagesList from './MentorMessagesList';
import MentorMessagesThread from './MentorMessagesThread';
import MentorMessagesComposer from './MentorMessagesComposer';
import MentorMessagesSidebar from './MentorMessagesSidebar';
import MentorPortalService, { MentorMenteeCard } from './MentorPortalService';
import MentorMessagesService, {
    MentorConversationRow,
    MentorMessageBubble,
} from './MentorMessagesService';
import { EMPTY_MENTOR_COPY } from './mentorDashboardFormat';
import './mentorDashboard.css';
import './mentorMentees.css';
import './mentorMessages.css';

const rowsFromMentees = (mentees: MentorMenteeCard[]): MentorConversationRow[] =>
    mentees.map((mentee) => ({
        mentee,
        conversationId: null,
        lastMessagePreview: null,
        lastSentDate: null,
        unreadLabel: '—',
    }));

const MentorMessages = () => {
    const [searchParams] = useSearchParams();
    const menteeFromUrl = String(searchParams.get('mentee') || '').trim() || null;
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);
    const [rows, setRows] = useState<MentorConversationRow[]>([]);
    const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
    const [messages, setMessages] = useState<MentorMessageBubble[]>([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);

    const selectedRow = rows.find((row) => row.mentee.menteeUuid === selectedUuid) || null;
    const selectedMentee = selectedRow?.mentee || null;

    const loadThread = (menteeUuid: string) => {
        setThreadLoading(true);
        setThreadError(null);
        setSendError(null);
        MentorMessagesService.getThread(menteeUuid)
            .then((payload) => {
                setMessages(payload.messages || []);
            })
            .catch((err: unknown) => {
                setThreadError(MentorMessagesService.errorMessage(err));
                setMessages([]);
            })
            .finally(() => setThreadLoading(false));
    };

    useEffect(() => {
        let cancelled = false;
        MentorPortalService.getMentees()
            .then((data) => {
                if (cancelled) return;
                const mentees = data.mentees || [];
                return MentorMessagesService.listConversations()
                    .then((listed) => {
                        if (cancelled) return;
                        setRows(listed);
                        setLoadError(null);
                        setForbidden(false);
                        const urlMatch =
                            menteeFromUrl &&
                            listed.some((row) => row.mentee.menteeUuid === menteeFromUrl)
                                ? menteeFromUrl
                                : null;
                        const first =
                            urlMatch ||
                            listed.find((row) => row.mentee.menteeUuid)?.mentee.menteeUuid ||
                            null;
                        setSelectedUuid(first);
                        if (first) loadThread(first);
                    })
                    .catch((err: unknown) => {
                        if (cancelled) return;
                        setRows(rowsFromMentees(mentees));
                        setLoadError(MentorMessagesService.errorMessage(err));
                    });
            })
            .catch((err: { status?: number; message?: string }) => {
                if (cancelled) return;
                if (err?.status === 403 || err?.status === 401) {
                    setForbidden(true);
                    setLoadError(null);
                    setRows([]);
                    return;
                }
                setLoadError(err?.message || 'Unable to load mentorship data.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [menteeFromUrl]);

    const onSelect = (menteeUuid: string) => {
        setSelectedUuid(menteeUuid);
        setDraft('');
        setSendError(null);
        loadThread(menteeUuid);
    };

    const onSend = () => {
        const uuid = selectedUuid;
        const text = draft.trim();
        if (!uuid || !text || sending) return;
        setSending(true);
        setSendError(null);
        MentorMessagesService.send(uuid, text)
            .then((payload) => {
                setMessages(payload.messages || []);
                setDraft('');
                setRows((current) =>
                    current.map((row) =>
                        row.mentee.menteeUuid === uuid
                            ? { ...row, lastMessagePreview: text, conversationId: payload.conversationId }
                            : row
                    )
                );
            })
            .catch((err: unknown) => {
                setSendError(MentorMessagesService.errorMessage(err));
            })
            .finally(() => setSending(false));
    };

    const isEmptyMentor = !loading && !loadError && forbidden;
    const isEmptyAssigned = !loading && !loadError && !forbidden && rows.length === 0;
    const showPanes = !loading && !forbidden && rows.length > 0;

    return (
        <div className="container py-4 mdash mmmsg">
            
            <MentorPortalNav />
            <MentorMessagesChrome />
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-3 gap-3">
              <div>
                <h4 className="font-22 font-600 mb-1 text-dark">Messages</h4>
                <div className="text-secondary small">Async conversations with your mentees · Offline queries are delivered when you next log in</div>
              </div>
            </div>
            {loading && <p className="mdash-empty">Loading…</p>}
            {!loading && loadError && rows.length === 0 ? (
                <p className="mdash-empty">{loadError}</p>
            ) : null}
            {isEmptyMentor && <p className="mdash-empty">{EMPTY_MENTOR_COPY}</p>}
            {isEmptyAssigned && <p className="mdash-empty">{EMPTY_MENTOR_COPY}</p>}
            {showPanes ? (
                <div className="row g-0 border border-light-subtle rounded-3 overflow-hidden bg-white shadow-sm" style={{minHeight:'580px'}}>
                    <MentorMessagesList
                        rows={rows}
                        selectedUuid={selectedUuid}
                        disabled={false}
                        onSelect={onSelect}
                    />
                    <div className="col-md-6 border-end border-light-subtle d-flex flex-column justify-content-between bg-light bg-opacity-25">
                        <MentorMessagesThread
                            menteeName={selectedMentee?.menteeName || null}
                            messages={messages}
                            loading={threadLoading}
                            error={threadError}
                        />
                        <MentorMessagesComposer
                            value={draft}
                            sending={sending}
                            disabled={!selectedUuid || threadLoading}
                            error={sendError}
                            onChange={setDraft}
                            onSend={onSend}
                        />
                    </div>
                    <MentorMessagesSidebar mentee={selectedMentee} />
                </div>
            ) : null}
        </div>
    );
};

export default MentorMessages;
